import React, { useState, useMemo, useEffect } from 'react';
import img from './../../assets/img/no_img.jpeg';

import { TabContent, TabPane, Nav, NavItem, NavLink } from 'reactstrap';
import classnames from 'classnames';
import IntlMessages from '../../util/IntlMessages';

import { FILE_URLS } from '../../constants/Common';
import { downloadFile, downloadVideo, isMobile } from '../../components/DiamondList/DiamondListFunctions';

import { ImageWithView } from 'components/DiamondDetail/ImageWithView';
import ZoomableImageView from 'components/DiamondDetail/ZoomableImageView';

const ImageSlider = (props) => {
  const { dnaData } = props;

  const [copyText, setCopyText] = useState('Copy URL');
  const [loadError, setLoadError] = useState({
    image: false,
    video: false,
    hAFile: false,
    arrowFile: false,
    assetFile: false,
    fluorescenceFile: false,
    blackBackgroundFile: false,
    certImg: false,
    plotImg: false,
  });
  const shared = window.location.search ? window.location.search.replace('?', '').split('&') : [];
  const hasSharedFlag = (flag) => Array.isArray(shared) && shared.some((item) => item === flag);

  const initialTab = useMemo(() => {
    if (hasSharedFlag('isImg=true')) return '1';
    if (hasSharedFlag('isVideo=true')) return '2';
    if (hasSharedFlag('isHa=true')) return '3';
    if (hasSharedFlag('isCert=true')) return '4';
    if (hasSharedFlag('isProportion=true')) return '5';
    if (hasSharedFlag('isArrow=true')) return '7';
    if (hasSharedFlag('isAsset=true')) return '8';
    if (hasSharedFlag('flsImage=true')) return '9';
    // if (hasSharedFlag('isBlackBackground=true')) return '10';
    // Default to Image when available; otherwise first available media tab
    if (dnaData.img) return '1';
    if (dnaData.videoFile) return '2';
    if (dnaData.hAFile) return '3';
    if (dnaData.certFile) return '4';
    if (dnaData.arrowFile) return '7';
    if (dnaData.assetFile) return '8';
    if (dnaData.fluorescenceFile) return '9';
    // if (dnaData.blackBackgroundFile) return '10';
    return '';
  }, [dnaData, shared]);

  const stoneIdentity = dnaData?.vStnId ?? dnaData?.id ?? '';
  const [activeTab, setActiveTab] = useState('');

  useEffect(() => {
    if (!initialTab) return;
    setActiveTab(initialTab);
  }, [stoneIdentity, initialTab]);
  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };
  const setError = (key) => setLoadError((prev) => ({ ...prev, [key]: true }));
  const renderImageContent = (src, options = {}) =>
    isMobile() ? (
      <ImageWithView src={src} />
    ) : (
      <ZoomableImageView src={src} initialZoom={options.zoom} />
    );

  const tabConfig = [
    {
      id: '1',
      label: <IntlMessages id="app.Image" />,
      paneClassName: 'hv-600',
      showContent: dnaData.img,
      showNav: dnaData.img,
      thumbSrc: loadError.image ? img : FILE_URLS.img.replace('***', dnaData.vStnId),
      onThumbError: () => setError('image'),
      content: renderImageContent(FILE_URLS.img.replace('***', dnaData.vStnId)),
    },
    {
      id: '2',
      label: <IntlMessages id="app.Video" />,
      paneClassName: 'mb-17',
      showContent: dnaData.videoFile,
      showNav: dnaData.videoFile,
      thumbSrc: loadError.video || loadError.image ? img : FILE_URLS.img.replace('***', dnaData.vStnId),
      onThumbError: () => setError('video'),
      content: (
        <div className="diamondDetailImageBox htmlViewImage dna-mobile">
          <iframe
            allowFullScreen
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            key={Math.random()}
            src={FILE_URLS.videoFile.replace('***', dnaData.vStnId)}
            title="vFile"
            onError={() => setError('video')}
          />
        </div>
      ),
      copyable: true,
      wrapContent: false,
    },
    {
      id: '3',
      label: 'Heart',
      showContent: dnaData.hAFile && dnaData.shpNm === 'ROUND',
      showNav: dnaData.hAFile && dnaData.shpNm === 'ROUND',
      thumbSrc: loadError.hAFile ? img : FILE_URLS.hAFile.replace('***', dnaData.vStnId),
      onThumbError: () => setError('hAFile'),
      content: renderImageContent(FILE_URLS.hAFile.replace('***', dnaData.vStnId)),
    },
    {
      id: '4',
      label: <IntlMessages id="app.Certificate" />,
      showContent: dnaData.certFile,
      showNav: dnaData.certFile,
      thumbSrc: loadError.certImg ? img : FILE_URLS.certImg.replace('***', dnaData.rptNo),
      onThumbError: () => setError('certImg'),
      content: renderImageContent(FILE_URLS.certImg.replace('***', dnaData.rptNo), { zoom: 1.4 }),
    },
    {
      id: '5',
      label: <IntlMessages id="app.Proportions" />,
      paneClassName: 'hv-600',
      showContent: dnaData.crt >= 1,
      showNav: dnaData.rptNo && dnaData.crt >= 1,
      thumbSrc: require('./../../assets/img/DiamondDetail/proportion.png'),
      content: (
        <>
          <div
            className="diamondDetailImageBox proportions"
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          >
            <div className="diamondDetailWrapper" style={{ padding: '0px' }}>
              <img src={require('./../../assets/img/DiamondDetail/proportion.png')} className="diamondImage" />
              <div className="top-labels-container">
                <div className=" label-left-length">L: {dnaData.length}</div>
                <div className=" label-top-width">W: {dnaData.width}</div>
                <div className="label label-right-height">H: {dnaData.height}</div>
              </div>
              <div className="sec-part-container">{dnaData.tblPer ? `${dnaData.tblPer}%` : ''}</div>
              <div className="third-part-container-right">{dnaData.depPer ? `${dnaData.depPer}%` : ''}</div>
              <div className="label-left-cHgt">{dnaData.cHgt ? `${dnaData.cHgt}%` : ''}</div>
              <div className="label-left-pHgt">{dnaData.pHgt ? `${dnaData.pHgt}%` : ''}</div>
              <div className="label-left-grdlPer">{dnaData.grdlPer ? `${dnaData.grdlPer}%` : ''}</div>
              <div className="label-left-strLn">{dnaData.strLn ? `${dnaData.strLn}%` : ''}</div>
              <div className="label-left-lwrHal">{dnaData.lwrHal ? `${dnaData.lwrHal}%` : ''}</div>
              <div className="label-left-cAng">{dnaData.cAng ? `${dnaData.cAng}°` : ''}</div>
              <div className="label-left-pAng">{dnaData.pAng ? `${dnaData.pAng}°` : ''}</div>
            </div>

            {/* // Diamond Detail Text */}
            <div className="label-left-text">
              {dnaData.isFcCol && dnaData.ratio && (
                <div className="label-left-ratio">
                  <span>
                    <b>Ratio :</b> {dnaData.ratio}
                  </span>
                </div>
              )}
              {dnaData.grdlDesc ? (
                <div className="label-left-grdlDesc">
                  <span>
                    <b>Girdle (Desc.) :</b> {dnaData.grdlDesc}
                  </span>
                </div>
              ) : null}
              {dnaData.kToSStr ? (
                <div className="label-left-kToSStr">
                  <span>
                    <b>Key To Symbol :</b> {dnaData.kToSStr}
                  </span>
                </div>
              ) : null}
              {dnaData.cmt ? (
                <div className="label-left-cmt">
                  <span>
                    <b>Comments :</b> {dnaData.cmt}
                  </span>
                </div>
              ) : null}
            </div>

            {dnaData.rptNo && dnaData.lbNm === 'GIA' && !loadError.plotImg && (
              <img
                className="plot-img"
                src={FILE_URLS.plotImg.replace('***', dnaData.rptNo)}
                onError={() => setError('plotImg')}
              />
            )}
          </div>
        </>
      ),
      wrapContent: false,
    },
    {
      id: '7',
      label: <IntlMessages id="app.Arrow" />,
      showContent: dnaData.arrowFile && dnaData.shpNm === 'ROUND',
      showNav: dnaData.arrowFile && dnaData.shpNm === 'ROUND',
      thumbSrc: loadError.arrowFile ? img : FILE_URLS.arrowFile.replace('***', dnaData.vStnId),
      onThumbError: () => setError('arrowFile'),
      content: renderImageContent(FILE_URLS.arrowFile.replace('***', dnaData.vStnId)),
    },
    {
      id: '8',
      label: <IntlMessages id="app.Asset" />,
      showContent: dnaData.assetFile,
      showNav: dnaData.assetFile,
      thumbSrc: loadError.assetFile ? img : FILE_URLS.assetFile.replace('***', dnaData.vStnId),
      onThumbError: () => setError('assetFile'),
      content: renderImageContent(FILE_URLS.assetFile.replace('***', dnaData.vStnId)),
    },
    {
      id: '9',
      label: <IntlMessages id="app.compare.flo" />,
      showContent: dnaData.fluorescenceFile,
      showNav: dnaData.fluorescenceFile,
      thumbSrc: loadError.fluorescenceFile ? img : FILE_URLS.fluorescenceFile.replace('***', dnaData.vStnId),
      onThumbError: () => setError('fluorescenceFile'),
      content: renderImageContent(FILE_URLS.fluorescenceFile.replace('***', dnaData.vStnId)),
    },
    // {
    //   id: '10',
    //   label: <IntlMessages id="app.blackBackground" />,
    //   showContent: dnaData.blackBackgroundFile,
    //   showNav: dnaData.blackBackgroundFile,
    //   thumbSrc: loadError.blackBackgroundFile ? img : FILE_URLS.blackBackgroundFile.replace('***', dnaData.vStnId),
    //   onThumbError: () => setError('blackBackgroundFile'),
    //   content: renderImageContent(FILE_URLS.blackBackgroundFile.replace('***', dnaData.vStnId)),
    // },
  ];
  const visibleContentTabs = tabConfig.filter((tab) => tab.showContent);
  const visibleNavTabs = tabConfig.filter((tab) => tab.showNav);

  return (
    <>
      <div className="diamondDetailLeft dna-left-sidebar">
        <div className="diamondDetailInnerDetail">
          {/* {shared.length > 1 ? ( */}
          <TabContent activeTab={activeTab}>
            {visibleContentTabs.map((tab) => {
              const content =
                tab.wrapContent === false ? tab.content : <div className="diamondDetailImageBox">{tab.content}</div>;
              return (
                <TabPane key={tab.id} tabId={tab.id} className={tab.paneClassName}>
                  {content}
                </TabPane>
              );
            })}
          </TabContent>
          {/* ) : (
            ''
          )} */}
          <hr />

          <Nav tabs className="DNAdiamondDetailTopTab">
            {visibleNavTabs.map((tab) => (
              <NavItem key={tab.id}>
                <NavLink className={classnames({ active: activeTab === tab.id })} onClick={() => toggle(tab.id)}>
                  <div className="DNAdiamondDetailTabText">
                    <div className="DNAdiamondDetailTabImage">
                      <img src={tab.thumbSrc} onError={tab.onThumbError} />
                    </div>
                    <div className={tab.id === '10' ? 'DNAdiamondDetailTabTextBlackBackground' : ''}>{tab.label}</div>
                  </div>
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>
      </div>
    </>
  );
};

export default ImageSlider;
